/**
 * 🎬 Crossover Showdown — Demo Match Storyboard (Phase 1)
 *
 * Auto-plays a step-by-step cinematic beat-by-beat of a fighter's
 * moveset against a Shadow Sparring Partner:
 *
 *   ROUND 1 · FIGHT → Jab → Straight → Specials (up to 3) →
 *   Ultimate 1 → Ultimate 2 (if any) → Humiliation → K.O.
 *
 * Each beat has a caption, a stage effect (emoji burst + shake), and
 * chips down the opponent's HP bar. Player can pause / next / prev.
 *
 * NO REAL COMBAT ENGINE — this is a canon-preview storyboard for
 * Phase 1 of the arcade fighting mode.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Play, Pause, SkipForward, SkipBack, RotateCcw } from "lucide-react";

const ACCENT_TO_HEX = {
  amber:   "#fbbf24",
  emerald: "#34d399",
  pink:    "#f472b6",
  slate:   "#cbd5e1",
  cyan:    "#22d3ee",
};

/** Build the ordered beat list from a fighter's canon data. */
function buildBeatList(fighter) {
  const beats = [];

  beats.push({
    kind: "roundstart",
    caption: "ROUND 1 · FIGHT!",
    burst: "💥",
    damage: 0,
    color: "text-amber-300",
  });

  beats.push({
    kind: "basic",
    caption: "Jab!",
    subcaption: "Basic Attack",
    burst: "👊",
    damage: 6,
    color: "text-neutral-200",
  });

  beats.push({
    kind: "basic",
    caption: "Straight!",
    subcaption: "Basic Attack",
    burst: "💢",
    damage: 8,
    color: "text-neutral-200",
  });

  // First 3 specials (fighter-specific)
  (fighter.specials || []).slice(0, 3).forEach((s) => {
    beats.push({
      kind: "special",
      caption: s.name,
      subcaption: s.input || "Special Move",
      burst: burstForSpecial(fighter, s),
      damage: 14,
      color: `text-${fighter.accent}-300`,
    });
  });

  // Ultimate 1
  const ults = fighter.ultimates || (fighter.ultimate ? [fighter.ultimate] : []);
  if (ults[0]) {
    beats.push({
      kind: "ultimate",
      caption: ults[0].name,
      subcaption: "ULTIMATE",
      burst: burstForUltimate(fighter, 0),
      damage: 28,
      color: `text-${fighter.accent}-300`,
      screenShake: true,
    });
  }

  // Ultimate 2
  if (ults[1]) {
    beats.push({
      kind: "ultimate",
      caption: ults[1].name,
      subcaption: "ULTIMATE 2",
      burst: burstForUltimate(fighter, 1),
      damage: 30,
      color: `text-${fighter.accent}-300`,
      screenShake: true,
    });
  }

  // Humiliation finisher
  const humiliation = (fighter.finishers || []).find(
    (f) =>
      /humiliation/i.test(f.tone || "") ||
      /humiliation/i.test(f.name || "")
  ) || (fighter.finishers || [])[0];

  if (humiliation) {
    beats.push({
      kind: "humiliation",
      caption: humiliation.name,
      subcaption: "HUMILIATION!",
      burst: "💀",
      damage: 100, // guaranteed K.O.
      color: "text-red-400",
      screenShake: true,
    });
  } else {
    beats.push({
      kind: "ko",
      caption: "K.O.",
      subcaption: "Victory",
      burst: "💥",
      damage: 100,
      color: "text-amber-300",
    });
  }

  return beats;
}

function burstForSpecial(fighter, s) {
  const name = (s.name || "").toLowerCase();
  if (name.includes("shush")) return "🤫";
  if (name.includes("pages") || name.includes("book")) return "📖";
  if (name.includes("canine") || name.includes("pounce")) return "🐕";
  if (name.includes("uppercut")) return "⬆️";
  if (name.includes("knee")) return "🦵";
  if (name.includes("elbow")) return "💢";
  if (name.includes("sweep")) return "🦶";
  if (name.includes("burst") || name.includes("fire") || name.includes("missile")) return "🔥";
  if (name.includes("mirror") || name.includes("reflect")) return "🪞";
  if (name.includes("satellite") || name.includes("summon")) return "🌟";
  if (name.includes("spiral") || name.includes("scarf")) return "🌀";
  if (name.includes("toss") || name.includes("flask")) return "💣";
  if (name.includes("trap")) return "🪤";
  if (name.includes("gust") || name.includes("wind")) return "🌬️";
  if (name.includes("restock")) return "🎒";
  if (name.includes("cyclone")) return "🌪️";
  if (name.includes("clutch") || name.includes("grab")) return "🤜";
  if (name.includes("flip") || name.includes("leap")) return "🕴️";
  return fighter.icon || "⚡";
}

function burstForUltimate(fighter, idx) {
  const ults = fighter.ultimates || (fighter.ultimate ? [fighter.ultimate] : []);
  const name = (ults[idx]?.name || "").toLowerCase();
  if (name.includes("obliteration")) return "🌑";
  if (name.includes("avalanche") || name.includes("inventory")) return "📦";
  if (name.includes("symphony") || name.includes("spectral")) return "✨";
  if (name.includes("shatter")) return "💥";
  if (name.includes("decapitation") || name.includes("knee")) return "🦵";
  if (name.includes("tactical") || name.includes("overhaul")) return "🗺️";
  if (name.includes("archivist") || name.includes("bookshelf")) return "📚";
  return "🏆";
}

export default function ShowdownDemoMatch({ fighter, onClose }) {
  const beats = useMemo(() => buildBeatList(fighter), [fighter]);
  const [stepIdx, setStepIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [ended, setEnded] = useState(false);
  const [shaking, setShaking] = useState(false);
  const timerRef = useRef(null);

  const accentHex = ACCENT_TO_HEX[fighter.accent] || "#fbbf24";

  // Cumulative damage → HP calc
  const opponentHp = useMemo(() => {
    const totalDamage = beats
      .slice(0, stepIdx + 1)
      .reduce((sum, b) => sum + (b.damage || 0), 0);
    return Math.max(0, 100 - totalDamage);
  }, [beats, stepIdx]);

  const currentBeat = beats[stepIdx];

  // Screen-shake trigger on ultimate/humiliation
  useEffect(() => {
    if (!currentBeat) return;
    if (currentBeat.screenShake) {
      setShaking(true);
      const t = setTimeout(() => setShaking(false), 600);
      return () => clearTimeout(t);
    }
  }, [currentBeat, stepIdx]);

  // Auto-play advance
  useEffect(() => {
    if (!playing || ended) return;
    timerRef.current = setTimeout(() => {
      setStepIdx((i) => {
        if (i >= beats.length - 1) {
          setEnded(true);
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, currentBeat?.kind === "ultimate" || currentBeat?.kind === "humiliation" ? 2600 : 1600);
    return () => clearTimeout(timerRef.current);
  }, [playing, stepIdx, currentBeat, beats.length, ended]);

  const next = useCallback(() => {
    if (stepIdx >= beats.length - 1) {
      setEnded(true);
      setPlaying(false);
      return;
    }
    setStepIdx((i) => i + 1);
  }, [stepIdx, beats.length]);

  const prev = useCallback(() => {
    if (stepIdx === 0) return;
    setStepIdx((i) => i - 1);
    setEnded(false);
    setPlaying(false);
  }, [stepIdx]);

  const restart = useCallback(() => {
    setStepIdx(0);
    setEnded(false);
    setPlaying(true);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-shush-in"
      data-testid="showdown-demo-modal"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-5xl rounded-2xl border-2 border-neutral-700 bg-gradient-to-br from-[#0a0a0a] via-[#0f0812] to-[#0a0a0a] p-6 md:p-10 shadow-2xl ${
          shaking ? "animate-demo-shake" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="showdown-demo-close"
          className="absolute top-3 right-3 p-2 rounded-full text-neutral-300 hover:bg-white/10 hover:text-white transition"
        >
          <X size={20} />
        </button>

        {/* Round label */}
        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">
            Crossover Showdown · Demo Match Storyboard
          </div>
          <div className="text-xs text-neutral-400 mt-1">
            Beat {stepIdx + 1} / {beats.length} — Phase 1 canon preview (no real combat engine)
          </div>
        </div>

        {/* HP Bars */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Player HP (never chipped in preview) */}
          <div>
            <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-1">
              <span className="uppercase tracking-widest" style={{ color: accentHex }}>
                {fighter.name}
              </span>
              <span>100 / 100</span>
            </div>
            <div className="h-3 rounded-full bg-black/60 border border-neutral-800 overflow-hidden">
              <div className="h-full" style={{ width: "100%", background: accentHex }} />
            </div>
          </div>

          {/* Opponent HP — ticks down each beat */}
          <div>
            <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-1">
              <span className="uppercase tracking-widest text-neutral-400">
                Shadow Sparring Partner
              </span>
              <span data-testid="showdown-demo-opponent-hp">{opponentHp} / 100</span>
            </div>
            <div className="h-3 rounded-full bg-black/60 border border-neutral-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-500 to-red-400 transition-all duration-500"
                style={{ width: `${opponentHp}%` }}
              />
            </div>
          </div>
        </div>

        {/* Arena */}
        <div className="relative rounded-xl border-2 border-neutral-800 bg-gradient-to-b from-[#050508] via-[#080610] to-[#050508] h-[280px] md:h-[340px] flex items-center justify-between px-8 md:px-16 overflow-hidden">
          {/* stage floor */}
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-neutral-900 to-transparent" />
          {/* Player fighter */}
          <div
            className={`text-8xl leading-none transition-transform ${
              currentBeat?.kind !== "roundstart" && stepIdx > 0
                ? "translate-x-2 animate-demo-lunge"
                : ""
            }`}
            key={`p1-${stepIdx}`}
            data-testid="showdown-demo-p1"
          >
            {fighter.icon}
          </div>

          {/* Center burst */}
          <div
            key={`burst-${stepIdx}`}
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          >
            <div
              className="text-[110px] md:text-[160px] leading-none animate-demo-burst"
              data-testid="showdown-demo-burst"
            >
              {currentBeat?.burst}
            </div>
            {currentBeat && (
              <div className="mt-2 text-center animate-demo-caption">
                <div
                  className={`text-2xl md:text-4xl font-black uppercase tracking-wider ${currentBeat.color}`}
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="showdown-demo-caption"
                >
                  {currentBeat.caption}
                </div>
                {currentBeat.subcaption && (
                  <div className="text-[10px] uppercase tracking-[0.35em] text-neutral-400 mt-1">
                    {currentBeat.subcaption}
                    {currentBeat.damage > 0 && ` · −${currentBeat.damage} HP`}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Opponent */}
          <div
            className={`text-8xl leading-none transition-transform ${
              opponentHp <= 0
                ? "opacity-30 grayscale rotate-90 translate-y-6"
                : stepIdx > 0
                ? "-translate-x-2 animate-demo-hit"
                : ""
            }`}
            key={`p2-${stepIdx}`}
            data-testid="showdown-demo-p2"
          >
            👤
          </div>

          {/* End state — K.O. banner */}
          {ended && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm"
              data-testid="showdown-demo-ko"
            >
              <div
                className="text-6xl md:text-8xl font-black text-red-400 tracking-widest animate-demo-ko"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {currentBeat?.kind === "humiliation" ? "HUMILIATION!" : "K.O."}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.35em] text-neutral-400">
                {fighter.name} wins
              </div>
              <button
                onClick={restart}
                data-testid="showdown-demo-restart"
                className="mt-6 px-5 py-2 rounded-lg border border-neutral-600 bg-white/5 hover:bg-white/10 text-neutral-200 text-xs uppercase tracking-widest flex items-center gap-2"
              >
                <RotateCcw size={12} /> Restart Demo
              </button>
            </div>
          )}
        </div>

        {/* Playback controls */}
        <div className="mt-5 flex items-center justify-center gap-2">
          <button
            onClick={prev}
            disabled={stepIdx === 0}
            data-testid="showdown-demo-prev"
            className="p-2 rounded border border-neutral-700 text-neutral-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <SkipBack size={16} />
          </button>
          <button
            onClick={() => setPlaying((v) => !v)}
            disabled={ended}
            data-testid="showdown-demo-playpause"
            className="px-4 py-2 rounded border border-neutral-600 bg-white/5 hover:bg-white/10 text-neutral-200 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-30"
          >
            {playing ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Play</>}
          </button>
          <button
            onClick={next}
            disabled={ended}
            data-testid="showdown-demo-next"
            className="p-2 rounded border border-neutral-700 text-neutral-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <SkipForward size={16} />
          </button>
        </div>

        {/* Beat timeline */}
        <div className="mt-4 grid grid-cols-8 gap-1">
          {beats.map((b, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition ${
                i < stepIdx
                  ? "bg-neutral-500"
                  : i === stepIdx
                  ? "bg-white"
                  : "bg-neutral-800"
              }`}
              title={b.caption}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
